// h5马甲包 bridge
// 文档链接：https://docs.google.com/document/d/10g0jOvRDJlz986QBfVlNTJxfRPvSw4fpM7JQNe2BWII/edit?tab=t.0#heading=h.xp24dij1jso
'use client';

interface BridgeMeta {
  timestamp?: number;
  source?: 'web' | 'native';
  replyTo?: string;
}

interface BridgeMessage<T = any> {
  id: string;
  type: 'request' | 'response' | 'event' | 'error' | 'handshake';
  action: string;
  payload?: T;
  meta?: BridgeMeta;
}

interface IComeAppBridge {
  getJSI(): ((json: string) => void | undefined);
  invoke<T = any>(action: string, payload?: any): Promise<T>;
  register(action: string, handler: (payload: any, msg?: BridgeMessage) => any): void;
  unregister(action: string, handler: (payload: any, msg?: BridgeMessage) => any): void;
  emit(action: string, e?: any): void;
  getStorage<T = any>(key: string): Promise<T>;
  setStorage<T = any>(key: string, value: T): Promise<void>;
  getFcmToken(): Promise<string>;
  getAppInstanceId(): Promise<string>;
}

export enum BridgeCode {
  SUCCESS = 0, // 调用成功
  UNKNOWN_ERROR = 1, // 未知错误
  INVALID_ACTION = 2, // 未定义的 action
  INVALID_PARAMS = 3, // 参数格式不对
  UNAUTHORIZED = 401, // 未鉴权 / token 校验失败
  FORBIDDEN = 403, // 禁止访问（权限不足）
  TIMEOUT = 408, // 调用超时
  NOT_IMPLEMENTED = 501, // Native 未实现该接口
  INTERNAL_ERROR = 500, // Native 内部错误
}

/**
 * H5 马甲包桥接适配层：
 * - Native 在 window 上挂载 comeAppOpJSB
 * - JS 通过调用 window.comeAppOpJSB.xxx() 与 Native 交互
 *
 * 这里提供一层 IComeAppBridge 兼容实现（invoke / emit / register / unregister），
 * 这样外层 index.ts 暴露的 API 可以保持不变。
 */
export class VestBridge implements IComeAppBridge {
  private get native() {
    if (typeof window === 'undefined') return undefined;
    return (window as any).comeAppOpJSB;
  }

  public getJSI(): ((json: string) => void | undefined) {
    // 马甲包没有 WebView JSI，但为了复用 `isInApp` 这类判断，
    // 当存在 comeAppOpJSB 时返回一个空函数，表示处于 App 环境。
    // 判断window是否存在comeAppOpJSB，如果存在则返回一个空函数，否则返回undefined
    // if (typeof window !== 'undefined' && (window as any).comeAppOpJSB) {
    //   return (window as any).comeAppOpJSB;
    // }
    return undefined as any;
  }

  /**
   * 调用 Native 方法
   * 约定：action 与 comeAppOpJSB 上的方法名一致，例如：
   *   invoke('getDeviceInfo') -> window.comeAppOpJSB.getDeviceInfo()
   *   invoke('setStorage', ['key', 'value']) -> window.comeAppOpJSB.setStorage('key', 'value')
   */
  public invoke<T = any>(action: string, payload?: any): Promise<T> {
    const native = this.native;
    return new Promise<T>((resolve, reject) => {
      if (!native) {
        reject({
          code: BridgeCode.NOT_IMPLEMENTED,
          message: 'comeAppOpJSB not found',
        });
        return;
      }

      const fn = (native as any)[action];
      if (typeof fn !== 'function') {
        reject({
          code: BridgeCode.INVALID_ACTION,
          message: `No native method for action: ${action}`,
        });
        return;
      }
      try {
        let result: any;
        if (payload === undefined) {
          result = native[action]?.();
        } else if (Array.isArray(payload)) {
          // 支持多参数调用：invoke('setStorage', ['key', 'value'])
          result = native[action]?.(...payload);
        } else {
          result = native[action]?.(payload);
        }

        // 兼容同步返回和 Promise
        if (result && typeof (result as any).then === 'function') {
          (result as Promise<T>).then(resolve).catch((err) => {
            reject({
              code: BridgeCode.INTERNAL_ERROR,
              message: err?.message || 'Native promise error',
            });
          });
        } else {
          // 如果是 string 则需要 stringfy
          if (result && typeof result === 'string') {
            try {
              resolve(JSON.parse(result) as T);
            } catch {
              resolve(result as T);
            }
          } else {
            resolve(result as T);
          }
        }
      } catch (err: any) {
        reject({
          code: BridgeCode.INTERNAL_ERROR,
          message: err?.message || 'Native call error',
        });
      }
    });
  }

  /**
   * 事件类调用：同样映射到 comeAppOpJSB 上的方法，忽略返回值。
   */
  public emit(action: string, e?: any): void {
    void this.invoke(action, e);
  }

  /**
   * 事件注册 / 反注册：目前没有统一马甲包协议，先仅在 JS 侧缓存。
   */
  private handlers: Record<string, (payload: any, msg?: BridgeMessage) => any> =
    {};

  public register(action: string, handler: (payload: any, msg?: BridgeMessage) => any) {
    this.handlers[action] = handler;
  }

  public unregister(action: string, _handler?: (payload: any, msg?: BridgeMessage) => any) {
    void _handler;
    delete this.handlers[action];
  }

  /**
   * 获取本地存储
   * @param key 存储键名
   * @returns Promise<T> 存储的值
   */
  public getStorage<T = any>(key: string): Promise<T> {
    return this.invoke<T>('getStorage', key);
  }

  /**
   * 设置本地存储
   * @param key 存储键名
   * @param value 存储的值
   */
  public setStorage<T = any>(key: string, value: T): Promise<void> {
    return this.invoke<void>('setStorage', [key, value]);
  }

  /**
   * 获取 FCM Token
   * @returns Promise<string> FCM Token
   */
  public getFcmToken(): Promise<string> {
    return this.invoke<string>('getFcmToken');
  }

  public getAppInstanceId(): Promise<string> {
    return this.invoke<string>('getAppInstanceId');
  }

}

const fn: (...args: any[]) => any = (_) => _;

export const bridge = typeof window !== 'undefined'
  ? new VestBridge()
  : {
    getJSI: undefined as any,
    invoke: fn,
    emit: fn,
    register: fn,
    unregister: fn,
    getStorage: fn,
    setStorage: fn,
    getFcmToken: fn,
    getAppInstanceId: fn,
  } as IComeAppBridge;
